import { Pencil, ShieldCheck, StickyNote, Trash2, Truck, UserRound, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { byId } from '../../../core/assignment-rules';
import { temporaryVehicleLabel } from '../../../core/external-people';
import {
  CREW_ROLE_LABELS,
  ESCORT_OPTION_LABELS,
  ESCORT_OPTION_TONES,
  EXTERNAL_PERSON_TAG,
  EXTERNAL_PERSON_TONE,
  TEMPORARY_VEHICLE_LABEL,
} from '../../../types/assignment';
import type { ExternalPerson, TaskCrewMember, TaskTeam } from '../../../types/assignment';
import { vehicleCallSign } from '../../../core/military-id';
import { VEHICLE_TYPE_LABELS } from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';

interface TaskTeamCardProps {
  team: TaskTeam;
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /** Omitted for a viewer who may not assign — the card is then read-only. */
  onEdit?: (team: TaskTeam) => void;
  onRemove?: (team: TaskTeam) => void;
}

/** One slot on the card: an icon, what it is, and who fills it. */
function Slot({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 text-ink/40 shrink-0" aria-hidden>
        {icon}
      </span>
      <span className="text-xs text-ink/50 shrink-0 w-12 mt-0.5">{label}</span>
      <div className="min-w-0 grow">{children}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <span className="text-sm text-ink/40">{text}</span>;
}

/** A soldier, the way a dispatcher says it: name then personal number. */
function SoldierLine({ soldier }: { soldier: Soldier }) {
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className="truncate font-medium">{soldier.fullName}</span>
      <span data-dir="ltr" className="text-xs text-ink/50 tabular-nums">
        {soldier.personalNumber}
      </span>
    </span>
  );
}

/**
 * Somebody off the roster, in whichever seat they hold. Marked "איש חוץ" in the
 * one tone the application reserves for it, and carrying the temporary vehicle
 * they arrived in — which is a fact about this mission, not a fleet record.
 */
function ExternalLine({ person }: { person: ExternalPerson }) {
  const vehicle = temporaryVehicleLabel(person);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 min-w-0">
      <span className="truncate font-medium">{person.fullName}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${EXTERNAL_PERSON_TONE}`}>
        {EXTERNAL_PERSON_TAG}
      </span>
      {person.personalNumber ? (
        <span data-dir="ltr" className="text-xs text-ink/50 tabular-nums">
          {person.personalNumber}
        </span>
      ) : null}
      {person.phone ? (
        <span data-dir="ltr" className="text-xs text-ink/50 tabular-nums">
          {person.phone}
        </span>
      ) : null}
      {person.role ? <span className="text-xs text-ink/50">{person.role}</span> : null}
      {vehicle ? (
        <span className="text-xs text-violet-700">
          {TEMPORARY_VEHICLE_LABEL}: {vehicle}
        </span>
      ) : null}
    </span>
  );
}

function Tag({ children, tone }: { children: ReactNode; tone?: string }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border max-w-full',
        tone ?? 'border-line bg-canvas text-ink/70',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/** One passenger chip — a roster soldier, or a manually added external rider. */
function PassengerTag({
  passenger,
  soldiers,
}: {
  passenger: TaskCrewMember;
  soldiers: readonly Soldier[];
}) {
  const soldier = byId(soldiers, passenger.soldierId);
  if (soldier) {
    return (
      <Tag>
        <span className="truncate">{soldier.fullName}</span>
        <span data-dir="ltr" className="text-ink/40 tabular-nums">
          {soldier.personalNumber}
        </span>
      </Tag>
    );
  }

  const external = passenger.externalPassenger;
  if (!external) return <Tag tone="border-rose-200 bg-rose-50 text-rose-700">נוסע לא מזוהה</Tag>;

  const vehicle = temporaryVehicleLabel(external);
  return (
    <Tag tone={EXTERNAL_PERSON_TONE}>
      <span className="truncate">{external.fullName}</span>
      {external.personalNumber ? (
        <span data-dir="ltr" className="text-violet-700/60 tabular-nums">
          {external.personalNumber}
        </span>
      ) : null}
      {external.role ? <span className="text-violet-700/60">{external.role}</span> : null}
      {vehicle ? (
        <span className="text-violet-700/70">
          {TEMPORARY_VEHICLE_LABEL}: {vehicle}
        </span>
      ) : null}
      <span className="text-[10px] rounded bg-violet-100 px-1 py-0.5">{EXTERNAL_PERSON_TAG}</span>
    </Tag>
  );
}

/**
 * One vehicle team inside a task: the vehicle it is built around, the driver,
 * the escort decision, everyone riding along and the team note. Everything on
 * this card belongs to this vehicle and to no other.
 */
export function TaskTeamCard({ team, vehicles, soldiers, onEdit, onRemove }: TaskTeamCardProps) {
  const vehicle = byId(vehicles, team.vehicleId);
  const driver = byId(soldiers, team.driverId);
  const escort = byId(soldiers, team.escortId);

  return (
    <article className="rounded-2xl border border-line bg-panel p-3 space-y-3 text-right">
      <header className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Truck size={18} className="text-brand-500 mt-0.5 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span
                data-dir="ltr"
                title="מספר פנימי"
                className="shrink-0 rounded-lg border border-brand-300 bg-brand-50 px-2 py-0.5 text-sm font-bold text-brand-700 tabular-nums"
              >
                {vehicle ? vehicleCallSign(vehicle) : '—'}
              </span>
              <h4 className="font-bold truncate">{vehicle ? vehicle.name : 'רכב לא ידוע'}</h4>
            </div>
            {vehicle ? (
              <p className="mt-0.5 text-xs text-ink/50">{VEHICLE_TYPE_LABELS[vehicle.vehicleType]}</p>
            ) : null}
          </div>
        </div>

        {onEdit || onRemove ? (
          <div className="flex items-center gap-1 shrink-0">
            {onEdit ? (
              <button
                type="button"
                onClick={() => onEdit(team)}
                title="עריכת צוות"
                className="w-8 h-8 rounded-lg border border-line flex items-center justify-center text-ink/60 hover:text-ink"
              >
                <Pencil size={15} aria-hidden />
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(team)}
                title="הסרת צוות"
                className="w-8 h-8 rounded-lg border border-line flex items-center justify-center text-rose-600 hover:bg-rose-50"
              >
                <Trash2 size={15} aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="space-y-2">
        <Slot icon={<UserRound size={15} />} label={CREW_ROLE_LABELS.driver}>
          {driver ? (
            <SoldierLine soldier={driver} />
          ) : team.externalDriver ? (
            <ExternalLine person={team.externalDriver} />
          ) : (
            <Empty text="טרם נקבע נהג" />
          )}
        </Slot>

        <Slot icon={<ShieldCheck size={15} />} label={CREW_ROLE_LABELS.escort}>
          {team.escortOption === 'soldier' && escort ? (
            <SoldierLine soldier={escort} />
          ) : team.escortOption === 'external' && team.externalEscort ? (
            <ExternalLine person={team.externalEscort} />
          ) : (
            <Tag tone={ESCORT_OPTION_TONES[team.escortOption]}>
              {ESCORT_OPTION_LABELS[team.escortOption]}
            </Tag>
          )}
        </Slot>

        <Slot icon={<Users size={15} />} label={`${CREW_ROLE_LABELS.passenger}ים`}>
          {team.passengers.length === 0 ? (
            <Empty text="אין נוסעים נוספים" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {team.passengers.map((passenger) => (
                <PassengerTag key={passenger.id} passenger={passenger} soldiers={soldiers} />
              ))}
            </div>
          )}
        </Slot>
      </div>

      {team.teamNote ? (
        <p className="flex items-start gap-2 rounded-xl border border-line bg-canvas p-2 text-xs text-ink/70">
          <StickyNote size={14} className="text-ink/40 mt-0.5 shrink-0" aria-hidden />
          <span className="min-w-0">{team.teamNote}</span>
        </p>
      ) : null}
    </article>
  );
}
